import React, { useLayoutEffect, useRef } from 'react';
import { Backdrop } from '@mui/material';
import { SidePanelPlacement } from 'features/side-panels/types';
import { useAnimate } from 'motion/react';
import { useBreakpoints } from 'hooks/useBreakpoints';
import { PanelWrapper, Root } from './styled';

export type SidePanelProps = {
  open: boolean;
  children: React.ReactNode;
  placement: SidePanelPlacement;
  onBackdropClick?: () => void;
};

const springTransition = { type: 'spring' as const, bounce: 0, duration: 0.3 };
const snapTransition = { duration: 0 };

const SidePanel = ({ children, open, onBackdropClick, placement }: SidePanelProps) => {
  const { isMobile } = useBreakpoints();
  const [scope, animate] = useAnimate<HTMLDivElement>();

  const isOpensRight = placement === 'bottom' || placement === 'left';
  const offscreenX = isOpensRight ? '-100%' : '100%';
  const targetX = open ? '0%' : offscreenX;

  const prevPlacementRef = useRef(placement);

  // Snap instantly on placement change (before browser paint) to prevent
  // the panel sweeping across the screen as the CSS anchor flips.
  // Animate normally on open/close.
  useLayoutEffect(() => {
    const placementChanged = placement !== prevPlacementRef.current;
    prevPlacementRef.current = placement;

    const transition = placementChanged ? snapTransition : springTransition;
    animate(scope.current, { x: targetX }, transition);
  }, [animate, scope, targetX, placement]);

  return (
    <Root
      isMobile={isMobile}
      open={open}
      role={isMobile ? 'presentation' : undefined}
      data-testid="side-panel-root"
    >
      {isMobile && (
        <Backdrop open={open} onClick={onBackdropClick} data-testid="side-panel-backdrop" />
      )}
      <PanelWrapper
        ref={scope}
        initial={{ x: offscreenX }}
        isMobile={isMobile}
        placement={placement}
        data-testid="side-panel"
      >
        {children}
      </PanelWrapper>
    </Root>
  );
};

export default SidePanel;
